import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { useSettings } from '@/context/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LayoutGrid, Loader2, Lock, Mail, User, CheckCircle2, ArrowLeft, Clock } from 'lucide-react';

export default function Register() {
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const brandName = settings?.brand_name || 'Commissioning Tools';
  const domain = settings?.domain || 'cxatools.com';
  const logo = settings?.logo;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await api.post('/auth/register', { email, name, password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cx-page flex items-center justify-center px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          {logo ? (
            <img src={logo} alt={brandName} className="h-12 w-12 rounded-2xl object-cover border border-[color:var(--cx-border)] shadow-[var(--cx-shadow-glow)]" />
          ) : (
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center shadow-[var(--cx-shadow-glow)]" style={{ background: 'var(--cx-brand-gradient)' }}>
              <LayoutGrid className="h-6 w-6 text-[#04120f]" />
            </div>
          )}
          <h1 className="mt-4 font-display text-2xl tracking-tight text-[color:var(--cx-fg)] text-center">{brandName}</h1>
          <p className="font-mono text-xs text-[color:var(--cx-muted)]">{domain} · request access</p>
        </div>

        {done ? (
          <div className="cx-glass rounded-2xl p-8 text-center shadow-[var(--cx-shadow-soft)]" data-testid="register-success">
            <div className="mx-auto h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(46,230,197,0.2), rgba(43,182,255,0.12))' }}>
              <Clock className="h-6 w-6 text-[color:var(--cx-primary)]" />
            </div>
            <h2 className="mt-4 font-display text-xl text-[color:var(--cx-fg)]">Request submitted</h2>
            <p className="mt-2 text-sm text-[color:var(--cx-fg-muted)]">
              Thanks! Your account is pending admin approval. You’ll be able to sign in once it’s approved.
            </p>
            <Button className="mt-6" onClick={() => navigate('/login')} data-testid="register-back-to-login"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to sign in</Button>
          </div>
        ) : (
          <div className="cx-glass rounded-2xl shadow-[var(--cx-shadow-soft)]">
            <div className="p-6 pb-2">
              <h2 className="font-display text-xl text-[color:var(--cx-fg)]">Create your account</h2>
              <p className="mt-1 text-sm text-[color:var(--cx-fg-muted)]">An admin will approve your access.</p>
            </div>
            <form onSubmit={submit} className="p-6 pt-4 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[color:var(--cx-fg-muted)]">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="pl-9" data-testid="register-name-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[color:var(--cx-fg-muted)]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@cxatools.com" className="pl-9" data-testid="register-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[color:var(--cx-fg-muted)]">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
                    <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="8+ characters" className="pl-9" autoComplete="new-password" data-testid="register-password-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[color:var(--cx-fg-muted)]">Confirm</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--cx-muted)]" />
                    <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className="pl-9" autoComplete="new-password" data-testid="register-confirm-input" />
                  </div>
                </div>
              </div>

              {error && <p className="text-sm text-[color:var(--destructive)]" data-testid="register-error">{error}</p>}

              <Button type="submit" disabled={submitting} className="w-full" data-testid="register-submit-button">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</>) : (<><CheckCircle2 className="h-4 w-4 mr-2" />Request access</>)}
              </Button>
            </form>
            <div className="px-6 pb-6 -mt-1 text-center text-sm text-[color:var(--cx-fg-muted)]">
              Already have access?{' '}
              <Link to="/login" data-testid="register-login-link" className="text-[color:var(--cx-primary)] hover:underline">Sign in</Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
